import React, {useCallback, useEffect, useState} from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { Logout } from "@mui/icons-material";
import axios from "axios";
import { Dots } from "react-activity";
import "react-activity/dist/library.css";

import { AUTH_ENDPOINT, COLOR_BRAND, IMAGES_ENDPOINT, BASE_CUSTOMER_URL, withNoCache } from "./consts";
import { getClientMetadata, SignIn } from "./components/SignIn";

const SESSION_HEARTBEAT_INTERVAL_MS = 15000;
let hasRecordedPortalVisit = false;

const requestUserHeartbeat = (idUser) => axios.post(
  AUTH_ENDPOINT + '?func=user_heartbeat&id_user=' + idUser,
  null,
  {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }
);

const isHeartbeatAccepted = (response) => (
  response.status === 200 && response.data !== false && response.data !== ""
);

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [customer, setCustomer] = useState({
    name: "Authenticating",
    color: "#000000",
    name: "Authenticating",
    username: "...",
    redirect: '?customer=8&user=35&is_admin=1'
  });
  const [redirectUrl, setRedirectUrl] = useState("");
  const classes = useStyles();
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [initOk, setInitOk] = useState(false);

  const clearSession = useCallback(() => {
    setIsAuthenticated(false);
    setCustomer({});
    localStorage.removeItem('signedIn');
    localStorage.removeItem('userData');
    setInitOk(true);
  }, []);

  useEffect(() => {
    if(!hasRecordedPortalVisit) {
      hasRecordedPortalVisit = true;

      getClientMetadata()
        .then((clientMetadata) => axios.post(
          AUTH_ENDPOINT + '?func=portal_visit',
          { client_metadata: clientMetadata },
          { headers: { 'Content-Type': 'application/json' } }
        ))
        .catch((e) => console.warn('Unable to record portal visit', e));
    }

    init();

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);

    // cleanup function to remove the event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const init = async () => {
    const isSignedIn = localStorage.getItem('signedIn');
    if(isSignedIn) {
      const userData = localStorage.getItem('userData');
      if(userData) {
        try {
          const parsedUserData = JSON.parse(userData);
          const heartbeatResponse = await requestUserHeartbeat(parsedUserData.id_user);
          if(isHeartbeatAccepted(heartbeatResponse)) {
            localStorage.setItem('userData', JSON.stringify(heartbeatResponse.data));
            const updatedCustomer = {
              ...heartbeatResponse.data,
              redirect:
                BASE_CUSTOMER_URL +
                heartbeatResponse.data.redirect +
                "/?id_customer=" +
                heartbeatResponse.data.id_customer +
                "&id_user=" +
                heartbeatResponse.data.id_user +
                "&is_admin=" +
                heartbeatResponse.data.is_admin
            };
            setCustomer(updatedCustomer);
            localStorage.setItem('userData', JSON.stringify(updatedCustomer));
            setIsAuthenticated(true);
            setInitOk(true);
          } else {
            clearSession();
          }
        } catch (e) {
          console.warn('Unable to restore session', e);
          clearSession();
        }
      } else {
        clearSession();
      }
    } else {
      setInitOk(true);
    }
  }

  useEffect(() => {
    if(customer.id_user){
      setIsAuthenticated(true);
    }
  }, [customer.id_user]);

  useEffect(() => {
    if(!isAuthenticated || !customer.id_user) {
      return undefined;
    }

    let isActive = true;

    const validateCurrentSession = async () => {
      try {
        const heartbeatResponse = await requestUserHeartbeat(customer.id_user);
        if(isActive && !isHeartbeatAccepted(heartbeatResponse)) {
          clearSession();
        }
      } catch (e) {
        console.warn('Unable to validate session', e);
      }
    };

    const heartbeatInterval = window.setInterval(
      validateCurrentSession,
      SESSION_HEARTBEAT_INTERVAL_MS
    );
    const validateOnFocus = () => validateCurrentSession();
    window.addEventListener('focus', validateOnFocus);

    return () => {
      isActive = false;
      window.clearInterval(heartbeatInterval);
      window.removeEventListener('focus', validateOnFocus);
    };
  }, [isAuthenticated, customer.id_user, clearSession]);

  const saveCustomerDetails = (data) => {
    setCustomer(data);
    localStorage.setItem('userData', JSON.stringify(data));
    localStorage.setItem('signedIn', true);
  }

  const signOut = async () => {
    try {
      await axios.post(AUTH_ENDPOINT + '?func=backoffice_logout');
    } catch {
      window.alert('Unable to sign out. Please try again.');
      return;
    }
    clearSession();
    window.location.href = "/";
  }

  function getFontColor(backgroundColor) {
    // Convert hex to RGB
    const rgb = parseInt(backgroundColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
  
    // Calculate luminance
    const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
    const luminance = 0.2126 * rNorm + 0.7152 * gNorm + 0.0722 * bNorm;
  
    // Determine contrast
    return luminance > 0.5 ? "#000000" : "#ffffff";
  }
  
  return (
    <>
      {initOk ? (
         <Router>
          {isAuthenticated ? (
            <>
              <div className={classes.header} style={{ backgroundColor: isAuthenticated ? customer.color : COLOR_BRAND }}>
                <div className={classes.logoContainer}>
                  <img src={IMAGES_ENDPOINT+"logo_signedIn.png"} className={classes.logo}/>
                </div>
                <div style={{ display: "flex", width: "100%", flex: 1, paddingLeft: 20, flexDirection: "row", alignItems: 'center' }}>
                  {isAuthenticated && (
                    <>
                      <div className={classes.app_name} style={{ color: getFontColor(customer.color) }}>{customer.name} &nbsp;<b style={{ fontSize: "9px" }}>({customer.username})</b></div>
                      <Logout className={classes.logout} style={{ color: getFontColor(customer.color) }} onClick={() => { signOut(); }} />
                    </>
                  )}
                </div>
              </div>
              <iframe className={classes.root} style={{ height: dimensions.height }} src={withNoCache(customer.redirect)} />
            </>
          ) : (
            <Routes>
              <Route path="" element={<SignIn setIsAuthenticated={setIsAuthenticated} saveCustomerDetails={saveCustomerDetails} />} />
            </Routes>
          )}
        </Router>
      ) : (
        <div style={{ display: "flex", height: "100%", width: "100%", alignItems: "center", justifyContent: "center" }}>
          <Dots style={{ color: "#00a2fd" }} />
        </div>
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    width: "100%",
    height: 40,
    alignItems: "center",
    position: "fixed"
  },
  headerTopPane: {
    width: '100%',
    height: 100,
    position: 'absolute',
    top: -100
  },
  root: {
    display: "block",
    flex: 1,
    width: "100%",
    height: "100%",
    borderWidth: 0
  },
  logoContainer: {
    display: "flex", 
    backgroundColor: "#ffffff",
    width: 120, 
    height: "100%", 
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
    '@media (max-width: 1024px)': {
      display: 'none'
    }
  },
  logo: {
    height: 'auto',
    width: "80%"
  },
  app_name: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 12
  },
  logout: {
    position: "absolute",
    right: 20,
    cursor: "pointer",
    '@media (max-width: 1024px)': {
      display: 'none'
    }
  }
}));
